import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  DollarSign, Lock, Unlock, FileText, ShoppingCart, TrendingDown,
  Plus, Package, RefreshCw, Banknote, CreditCard, Clock,
  ArrowDownCircle, ArrowUpCircle, Boxes, Minus, AlertTriangle
} from 'lucide-react';

const CuadreDiario = () => {
  const [resumen, setResumen] = useState(null);
  const [suministros, setSuministros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);

  // Dialogs
  const [ventaDialog, setVentaDialog] = useState(false);
  const [compraDialog, setCompraDialog] = useState(false);
  const [gastoDialog, setGastoDialog] = useState(false);
  const [cerrarDialog, setCerrarDialog] = useState(false);

  // Forms
  const [ventaForm, setVentaForm] = useState({ suministro_id: '', cantidad: 1, precio_venta: 0, metodo_pago: 'efectivo' });
  const [compraForm, setCompraForm] = useState({ suministro_id: '', cantidad: 1, precio_compra: 0, proveedor: '', notas: '' });
  const [gastoForm, setGastoForm] = useState({ descripcion: '', monto: 0, categoria: 'operacional' });
  const [cerrarForm, setCerrarForm] = useState({ efectivo_caja: 0, etiqueta: 'normal' });

  const fetchResumen = useCallback(async () => {
    try {
      const res = await api.get('/cuadre/resumen-dia', { params: { fecha } });
      setResumen(res.data);
    } catch (error) {
      console.error('Error loading resumen');
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  const fetchSuministros = async () => {
    try {
      const res = await api.get('/suministros/catalogo');
      let data = res.data || [];
      if (data.length === 0) {
        // Seed defaults
        const seedRes = await api.post('/cuadre/seed-suministros');
        data = seedRes.data?.data || [];
      }
      setSuministros(data);
    } catch (error) {
      console.error('Error loading suministros');
    }
  };

  useEffect(() => {
    fetchResumen();
    fetchSuministros();
  }, [fetchResumen]);

  const isToday = fecha === new Date().toISOString().split('T')[0];
  const cuadreAbierto = resumen?.cuadre?.estado === 'abierto';
  const cuadreCerrado = resumen?.cuadre?.estado === 'cerrado';
  const t = resumen?.totales || {};

  // === ACTIONS ===
  const handleAbrirCuadre = async () => {
    try {
      await api.post('/cuadre/abrir');
      toast.success('Cuadre del dia abierto');
      fetchResumen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir cuadre');
    }
  };

  const handleCerrarCuadre = async () => {
    try {
      await api.post('/cuadre/cerrar', cerrarForm);
      toast.success('Cuadre cerrado exitosamente');
      setCerrarDialog(false);
      fetchResumen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar cuadre');
    }
  };

  const handleVentaRapida = async (e) => {
    e.preventDefault();
    if (!ventaForm.suministro_id) { toast.error('Selecciona un suministro'); return; }
    try {
      await api.post('/cuadre/venta-rapida', ventaForm);
      toast.success('Venta registrada');
      setVentaDialog(false);
      setVentaForm({ suministro_id: '', cantidad: 1, precio_venta: 0, metodo_pago: 'efectivo' });
      fetchResumen();
      fetchSuministros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar venta');
    }
  };

  const handleQuickSell = async (suministro) => {
    try {
      await api.post('/cuadre/venta-rapida', {
        suministro_id: suministro.id,
        cantidad: 1,
        precio_venta: suministro.precio_venta,
        metodo_pago: 'efectivo',
      });
      toast.success(`${suministro.nombre} vendido - $${suministro.precio_venta.toFixed(2)}`);
      fetchResumen();
      fetchSuministros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleCompra = async (e) => {
    e.preventDefault();
    if (!compraForm.suministro_id) { toast.error('Selecciona un suministro'); return; }
    try {
      await api.post('/cuadre/compra-suministro', compraForm);
      toast.success('Compra registrada, stock actualizado');
      setCompraDialog(false);
      setCompraForm({ suministro_id: '', cantidad: 1, precio_compra: 0, proveedor: '', notas: '' });
      fetchResumen();
      fetchSuministros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    }
  };

  const handleGasto = async (e) => {
    e.preventDefault();
    if (!gastoForm.descripcion || gastoForm.monto <= 0) { toast.error('Completa los datos'); return; }
    try {
      await api.post('/cuadre/gasto-rapido', gastoForm);
      toast.success('Gasto registrado');
      setGastoDialog(false);
      setGastoForm({ descripcion: '', monto: 0, categoria: 'operacional' });
      fetchResumen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar gasto');
    }
  };

  const selectSupplyForSale = (id) => {
    const s = suministros.find(x => x.id === id);
    setVentaForm(prev => ({ ...prev, suministro_id: id, precio_venta: s?.precio_venta || 0 }));
  };

  const selectSupplyForPurchase = (id) => {
    const s = suministros.find(x => x.id === id);
    setCompraForm(prev => ({ ...prev, suministro_id: id, precio_compra: s?.precio_compra || 0 }));
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-3 sm:p-6 pb-24" data-testid="cuadre-diario-page">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* HEADER */}
        <Card className="shadow-xl border-2 border-blue-100">
          <CardHeader className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white rounded-t-lg py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <DollarSign className="h-7 w-7" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">Cuadre Diario</CardTitle>
                  <p className="text-blue-200 text-sm">Desglose completo del dia</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid="fecha-cuadre"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="h-10 bg-white/20 border-white/30 text-white w-40 [&::-webkit-calendar-picker-indicator]:invert"
                />
                <Button data-testid="refresh-cuadre" variant="secondary" size="sm" onClick={fetchResumen} className="h-10 bg-white/20 text-white hover:bg-white/30 border-white/30">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                {isToday && !resumen?.cuadre && (
                  <Button data-testid="abrir-cuadre-btn" onClick={handleAbrirCuadre} className="h-10 bg-green-500 hover:bg-green-600 text-white">
                    <Unlock className="mr-2 h-4 w-4" /> Abrir Cuadre
                  </Button>
                )}
                {cuadreAbierto && (
                  <Button data-testid="cerrar-cuadre-btn" onClick={() => setCerrarDialog(true)} className="h-10 bg-red-500 hover:bg-red-600 text-white">
                    <Lock className="mr-2 h-4 w-4" /> Cerrar Cuadre
                  </Button>
                )}
                {cuadreCerrado && (
                  <Badge className="bg-red-500/80 text-white text-sm px-3 py-1.5">Cerrado {resumen.cuadre.hora_cierre}</Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard icon={<FileText className="h-5 w-5" />} label="Facturas" value={t.facturas_count || 0} sublabel={`$${(t.total_facturas || 0).toFixed(2)}`} color="blue" testId="card-facturas" />
          <SummaryCard icon={<Banknote className="h-5 w-5" />} label="Efectivo" value={`$${(t.total_efectivo || 0).toFixed(2)}`} color="green" testId="card-efectivo" />
          <SummaryCard icon={<CreditCard className="h-5 w-5" />} label="Zelle" value={`$${(t.total_zelle || 0).toFixed(2)}`} color="purple" testId="card-zelle" />
          <SummaryCard icon={<Clock className="h-5 w-5" />} label="Pendiente" value={`$${(t.total_pendiente || 0).toFixed(2)}`} color="amber" testId="card-pendiente" />
          <SummaryCard icon={<ShoppingCart className="h-5 w-5" />} label="Suministros" value={`$${(t.total_ventas_suministro || 0).toFixed(2)}`} color="cyan" testId="card-suministros" />
          <SummaryCard icon={<TrendingDown className="h-5 w-5" />} label="Gastos" value={`$${(t.total_gastos || 0).toFixed(2)}`} color="red" testId="card-gastos" />
        </div>

        {/* BALANCE BAR */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-4 flex items-center justify-between text-white shadow-lg" data-testid="balance-bar">
          <div className="flex items-center gap-3">
            <DollarSign className="h-8 w-8" />
            <div>
              <p className="text-sm text-blue-200 font-medium">BALANCE DEL DIA</p>
              <p className="text-xs text-blue-300">Cobrado + Suministros - Gastos - Compras</p>
            </div>
          </div>
          <p className="text-3xl sm:text-4xl font-black" data-testid="balance-total">${(t.balance || 0).toFixed(2)}</p>
        </div>

        {/* ACTION BUTTONS (only when cuadre is open or today) */}
        {isToday && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Button data-testid="btn-venta-suministro" onClick={() => setVentaDialog(true)} className="h-14 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm flex flex-col items-center justify-center gap-1">
              <ShoppingCart className="h-5 w-5" />
              Vender Suministro
            </Button>
            <Button data-testid="btn-compra-suministro" onClick={() => setCompraDialog(true)} className="h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm flex flex-col items-center justify-center gap-1">
              <Boxes className="h-5 w-5" />
              Comprar / Restock
            </Button>
            <Button data-testid="btn-gasto-rapido" onClick={() => setGastoDialog(true)} className="h-14 bg-red-500 hover:bg-red-600 text-white font-bold text-sm flex flex-col items-center justify-center gap-1">
              <TrendingDown className="h-5 w-5" />
              Registrar Gasto
            </Button>
            <Button data-testid="btn-refresh" onClick={() => { fetchResumen(); fetchSuministros(); }} variant="outline" className="h-14 font-bold text-sm flex flex-col items-center justify-center gap-1 border-2">
              <RefreshCw className="h-5 w-5" />
              Actualizar
            </Button>
          </div>
        )}

        {/* QUICK SELL GRID */}
        {isToday && suministros.length > 0 && (
          <Card className="border-2 border-cyan-100">
            <CardHeader className="py-3 bg-cyan-50 border-b border-cyan-100">
              <CardTitle className="text-base flex items-center gap-2 text-cyan-800">
                <ShoppingCart className="h-5 w-5" /> Venta Rapida de Suministros
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {suministros.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`quick-sell-${s.nombre.replace(/\s/g, '-').toLowerCase()}`}
                    onClick={() => handleQuickSell(s)}
                    className="relative p-3 rounded-xl border-2 border-gray-200 hover:border-cyan-400 hover:bg-cyan-50 transition-all text-left group"
                  >
                    <p className="font-bold text-sm text-gray-800 truncate">{s.nombre}</p>
                    <p className="text-lg font-black text-cyan-600">${s.precio_venta?.toFixed(2)}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-xs font-semibold ${s.stock_actual <= s.stock_minimo ? 'text-red-500' : 'text-gray-400'}`}>
                        Stock: {s.stock_actual}
                      </span>
                      {s.stock_actual <= s.stock_minimo && <AlertTriangle className="h-3 w-3 text-red-500" />}
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-cyan-500/0 group-hover:bg-cyan-500/5 transition-colors" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FACTURAS DEL DIA */}
        <Card className="border-2 border-blue-100">
          <CardHeader className="py-3 bg-blue-50 border-b border-blue-100">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <FileText className="h-5 w-5" /> Facturas del Dia ({resumen?.facturas?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(!resumen?.facturas || resumen.facturas.length === 0) ? (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No hay facturas para esta fecha</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-50/50">
                      <TableHead className="font-bold text-xs">Factura</TableHead>
                      <TableHead className="font-bold text-xs">Remitente</TableHead>
                      <TableHead className="font-bold text-xs">Destinatario</TableHead>
                      <TableHead className="font-bold text-xs text-right">Total</TableHead>
                      <TableHead className="font-bold text-xs text-center">Metodo</TableHead>
                      <TableHead className="font-bold text-xs text-right">Cobrado</TableHead>
                      <TableHead className="font-bold text-xs text-right">Pendiente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resumen.facturas.map((f) => (
                      <TableRow key={f.id} data-testid={`factura-row-${f.numero_factura}`}>
                        <TableCell className="font-bold text-blue-600">{f.numero_factura}</TableCell>
                        <TableCell className="text-sm truncate max-w-[120px]">{f.remitente_nombre}</TableCell>
                        <TableCell className="text-sm truncate max-w-[120px]">{f.destinatario_nombre}</TableCell>
                        <TableCell className="text-right font-bold">${f.total?.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <MetodoPagoBadge metodo={f.metodo_pago} />
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-600">${f.monto_pagado?.toFixed(2)}</TableCell>
                        <TableCell className={`text-right font-bold ${f.monto_pendiente > 0 ? 'text-red-600' : 'text-gray-300'}`}>
                          ${f.monto_pendiente?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-gray-100 font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right text-sm">TOTALES:</TableCell>
                      <TableCell className="text-right text-lg">${(t.total_facturas || 0).toFixed(2)}</TableCell>
                      <TableCell />
                      <TableCell className="text-right text-lg text-green-600">${(t.total_cobrado || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-lg text-red-600">${(t.total_pendiente || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* DESGLOSE POR METODO DE PAGO */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Payment Breakdown */}
          <Card className="border-2 border-green-100">
            <CardHeader className="py-3 bg-green-50 border-b border-green-100">
              <CardTitle className="text-base flex items-center gap-2 text-green-800">
                <Banknote className="h-5 w-5" /> Desglose de Cobros
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <DesgloseRow label="Efectivo" value={t.total_efectivo} color="green" icon={<Banknote className="h-4 w-4" />} />
              <DesgloseRow label="Zelle" value={t.total_zelle} color="purple" icon={<CreditCard className="h-4 w-4" />} />
              <DesgloseRow label="Tarjeta" value={t.total_tarjeta} color="blue" icon={<CreditCard className="h-4 w-4" />} />
              {(t.total_otros || 0) > 0 && <DesgloseRow label="Otros" value={t.total_otros} color="gray" icon={<DollarSign className="h-4 w-4" />} />}
              <div className="border-t-2 pt-2 flex justify-between items-center">
                <span className="font-bold text-gray-700">Total Cobrado</span>
                <span className="text-xl font-black text-green-600">${(t.total_cobrado || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-red-600">
                <span className="font-bold">Pendiente Credito</span>
                <span className="text-xl font-black">${(t.total_pendiente || 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Ventas de Suministros */}
          <Card className="border-2 border-cyan-100">
            <CardHeader className="py-3 bg-cyan-50 border-b border-cyan-100">
              <CardTitle className="text-base flex items-center gap-2 text-cyan-800">
                <ShoppingCart className="h-5 w-5" /> Ventas de Suministros ({resumen?.ventas_suministro?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {(!resumen?.ventas_suministro || resumen.ventas_suministro.length === 0) ? (
                <p className="text-center text-gray-400 py-4 text-sm">No hay ventas de suministros</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {resumen.ventas_suministro.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        {(v.items || []).map((item, i) => (
                          <span key={i} className="font-semibold">{item.nombre || item.supply_id} x{item.cantidad}</span>
                        ))}
                        <span className="text-gray-400 ml-2 text-xs">({v.metodo_pago})</span>
                      </div>
                      <span className="font-bold text-cyan-600">${v.total?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold text-gray-700">Total Suministros</span>
                <span className="text-xl font-black text-cyan-600">${(t.total_ventas_suministro || 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* GASTOS + COMPRAS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Gastos */}
          <Card className="border-2 border-red-100">
            <CardHeader className="py-3 bg-red-50 border-b border-red-100">
              <CardTitle className="text-base flex items-center gap-2 text-red-800">
                <TrendingDown className="h-5 w-5" /> Gastos del Dia ({resumen?.gastos?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {(!resumen?.gastos || resumen.gastos.length === 0) ? (
                <p className="text-center text-gray-400 py-4 text-sm">No hay gastos registrados</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {resumen.gastos.map((g) => (
                    <div key={g.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <span className="font-semibold">{g.descripcion}</span>
                      <span className="font-bold text-red-600">-${g.monto?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold text-gray-700">Total Gastos</span>
                <span className="text-xl font-black text-red-600">-${(t.total_gastos || 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Compras de Suministros */}
          <Card className="border-2 border-indigo-100">
            <CardHeader className="py-3 bg-indigo-50 border-b border-indigo-100">
              <CardTitle className="text-base flex items-center gap-2 text-indigo-800">
                <Boxes className="h-5 w-5" /> Compras / Restock ({resumen?.compras_suministro?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {(!resumen?.compras_suministro || resumen.compras_suministro.length === 0) ? (
                <p className="text-center text-gray-400 py-4 text-sm">No hay compras registradas</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {resumen.compras_suministro.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                      <div>
                        <span className="font-semibold">{c.nombre_suministro}</span>
                        <span className="text-gray-400 ml-1 text-xs">x{c.cantidad}</span>
                        {c.proveedor && <span className="text-gray-400 ml-2 text-xs">({c.proveedor})</span>}
                      </div>
                      <span className="font-bold text-indigo-600">-${c.total?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold text-gray-700">Total Compras</span>
                <span className="text-xl font-black text-indigo-600">-${(t.total_compras || 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* STOCK DE SUMINISTROS */}
        <Card className="border-2 border-gray-200">
          <CardHeader className="py-3 bg-gray-50 border-b">
            <CardTitle className="text-base flex items-center gap-2 text-gray-800">
              <Package className="h-5 w-5" /> Inventario de Suministros
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold text-xs">Producto</TableHead>
                    <TableHead className="font-bold text-xs text-center">Stock</TableHead>
                    <TableHead className="font-bold text-xs text-right">P. Compra</TableHead>
                    <TableHead className="font-bold text-xs text-right">P. Venta</TableHead>
                    <TableHead className="font-bold text-xs text-center">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suministros.map((s) => (
                    <TableRow key={s.id} data-testid={`stock-row-${s.id}`}>
                      <TableCell className="font-bold">{s.nombre}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-lg font-bold ${s.stock_actual <= s.stock_minimo ? 'text-red-600' : 'text-green-600'}`}>
                          {s.stock_actual}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-gray-500">${s.precio_compra?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-semibold">${s.precio_venta?.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        {s.stock_actual <= s.stock_minimo ? (
                          <Badge className="bg-red-100 text-red-700 border-red-300 text-xs">Bajo</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">OK</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* =================== DIALOGS =================== */}

        {/* VENTA SUMINISTRO DIALOG */}
        <Dialog open={ventaDialog} onOpenChange={setVentaDialog}>
          <DialogContent className="max-w-md" data-testid="venta-dialog" aria-describedby="venta-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-cyan-600" /> Vender Suministro
              </DialogTitle>
              <p id="venta-desc" className="text-sm text-gray-500">Registrar venta de suministro</p>
            </DialogHeader>
            <form onSubmit={handleVentaRapida} className="space-y-4">
              <div>
                <Label className="font-semibold">Producto *</Label>
                <Select value={ventaForm.suministro_id} onValueChange={selectSupplyForSale}>
                  <SelectTrigger data-testid="venta-select-producto" className="h-11 mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {suministros.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre} (Stock: {s.stock_actual}) - ${s.precio_venta?.toFixed(2)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Cantidad</Label>
                  <Input data-testid="venta-cantidad" type="number" min="1" value={ventaForm.cantidad} onChange={(e) => setVentaForm(p => ({...p, cantidad: parseInt(e.target.value) || 1}))} className="h-11 mt-1" />
                </div>
                <div>
                  <Label className="font-semibold">Precio Venta</Label>
                  <Input data-testid="venta-precio" type="number" step="0.01" min="0" value={ventaForm.precio_venta || ''} onChange={(e) => setVentaForm(p => ({...p, precio_venta: parseFloat(e.target.value) || 0}))} className="h-11 mt-1" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Metodo de Pago</Label>
                <Select value={ventaForm.metodo_pago} onValueChange={(v) => setVentaForm(p => ({...p, metodo_pago: v}))}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-right text-lg font-bold text-cyan-600">
                Total: ${((ventaForm.cantidad || 0) * (ventaForm.precio_venta || 0)).toFixed(2)}
              </div>
              <Button data-testid="submit-venta" type="submit" className="w-full h-11 bg-cyan-600 hover:bg-cyan-700">
                <ShoppingCart className="mr-2 h-4 w-4" /> Registrar Venta
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* COMPRA / RESTOCK DIALOG */}
        <Dialog open={compraDialog} onOpenChange={setCompraDialog}>
          <DialogContent className="max-w-md" data-testid="compra-dialog" aria-describedby="compra-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Boxes className="h-5 w-5 text-indigo-600" /> Comprar / Restock
              </DialogTitle>
              <p id="compra-desc" className="text-sm text-gray-500">Registrar compra y reponer stock</p>
            </DialogHeader>
            <form onSubmit={handleCompra} className="space-y-4">
              <div>
                <Label className="font-semibold">Producto *</Label>
                <Select value={compraForm.suministro_id} onValueChange={selectSupplyForPurchase}>
                  <SelectTrigger data-testid="compra-select-producto" className="h-11 mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {suministros.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre} (Stock: {s.stock_actual})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Cantidad</Label>
                  <Input data-testid="compra-cantidad" type="number" min="1" value={compraForm.cantidad} onChange={(e) => setCompraForm(p => ({...p, cantidad: parseInt(e.target.value) || 1}))} className="h-11 mt-1" />
                </div>
                <div>
                  <Label className="font-semibold">Precio Compra</Label>
                  <Input data-testid="compra-precio" type="number" step="0.01" min="0" value={compraForm.precio_compra || ''} onChange={(e) => setCompraForm(p => ({...p, precio_compra: parseFloat(e.target.value) || 0}))} className="h-11 mt-1" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Proveedor</Label>
                <Input data-testid="compra-proveedor" value={compraForm.proveedor} onChange={(e) => setCompraForm(p => ({...p, proveedor: e.target.value}))} placeholder="Nombre del proveedor (opcional)" className="h-11 mt-1" />
              </div>
              <div>
                <Label className="font-semibold">Notas</Label>
                <Input data-testid="compra-notas" value={compraForm.notas} onChange={(e) => setCompraForm(p => ({...p, notas: e.target.value}))} placeholder="Notas opcionales" className="h-11 mt-1" />
              </div>
              <div className="text-right text-lg font-bold text-indigo-600">
                Total: ${((compraForm.cantidad || 0) * (compraForm.precio_compra || 0)).toFixed(2)}
              </div>
              <Button data-testid="submit-compra" type="submit" className="w-full h-11 bg-indigo-600 hover:bg-indigo-700">
                <Boxes className="mr-2 h-4 w-4" /> Registrar Compra
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* GASTO RAPIDO DIALOG */}
        <Dialog open={gastoDialog} onOpenChange={setGastoDialog}>
          <DialogContent className="max-w-md" data-testid="gasto-dialog" aria-describedby="gasto-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" /> Registrar Gasto
              </DialogTitle>
              <p id="gasto-desc" className="text-sm text-gray-500">Registrar un gasto del dia</p>
            </DialogHeader>
            <form onSubmit={handleGasto} className="space-y-4">
              <div>
                <Label className="font-semibold">Descripcion *</Label>
                <Input data-testid="gasto-descripcion" value={gastoForm.descripcion} onChange={(e) => setGastoForm(p => ({...p, descripcion: e.target.value}))} placeholder="Ej: Gasolina, Almuerzo, Peaje..." className="h-11 mt-1" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Monto *</Label>
                  <Input data-testid="gasto-monto" type="number" step="0.01" min="0.01" value={gastoForm.monto || ''} onChange={(e) => setGastoForm(p => ({...p, monto: parseFloat(e.target.value) || 0}))} placeholder="0.00" className="h-11 mt-1" required />
                </div>
                <div>
                  <Label className="font-semibold">Categoria</Label>
                  <Select value={gastoForm.categoria} onValueChange={(v) => setGastoForm(p => ({...p, categoria: v}))}>
                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operacional">Operacional</SelectItem>
                      <SelectItem value="combustible">Combustible</SelectItem>
                      <SelectItem value="comida">Comida</SelectItem>
                      <SelectItem value="transporte">Transporte</SelectItem>
                      <SelectItem value="oficina">Oficina</SelectItem>
                      <SelectItem value="otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="submit-gasto" type="submit" className="w-full h-11 bg-red-500 hover:bg-red-600">
                <TrendingDown className="mr-2 h-4 w-4" /> Registrar Gasto
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* CERRAR CUADRE DIALOG */}
        <Dialog open={cerrarDialog} onOpenChange={setCerrarDialog}>
          <DialogContent className="max-w-md" data-testid="cerrar-dialog" aria-describedby="cerrar-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-red-600" /> Cerrar Cuadre del Dia
              </DialogTitle>
              <p id="cerrar-desc" className="text-sm text-gray-500">Confirma el efectivo en caja y cierra el cuadre</p>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border space-y-2">
                <div className="flex justify-between"><span className="text-gray-600">Total Cobrado:</span><span className="font-bold text-green-600">${(t.total_cobrado || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Suministros:</span><span className="font-bold text-cyan-600">${(t.total_ventas_suministro || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Gastos:</span><span className="font-bold text-red-600">-${(t.total_gastos || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Compras:</span><span className="font-bold text-indigo-600">-${(t.total_compras || 0).toFixed(2)}</span></div>
                <div className="border-t pt-2 flex justify-between"><span className="font-bold text-lg">Balance:</span><span className="font-black text-xl text-blue-700">${(t.balance || 0).toFixed(2)}</span></div>
              </div>
              <div>
                <Label className="font-semibold">Efectivo en Caja</Label>
                <Input data-testid="cerrar-efectivo" type="number" step="0.01" min="0" value={cerrarForm.efectivo_caja || ''} onChange={(e) => setCerrarForm(p => ({...p, efectivo_caja: parseFloat(e.target.value) || 0}))} placeholder="0.00" className="h-11 mt-1" />
              </div>
              <div>
                <Label className="font-semibold">Etiqueta del Dia</Label>
                <Select value={cerrarForm.etiqueta} onValueChange={(v) => setCerrarForm(p => ({...p, etiqueta: v}))}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="bueno">Bueno</SelectItem>
                    <SelectItem value="excelente">Excelente</SelectItem>
                    <SelectItem value="malo">Malo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="submit-cerrar" onClick={handleCerrarCuadre} className="w-full h-11 bg-red-500 hover:bg-red-600 text-white">
                <Lock className="mr-2 h-4 w-4" /> Cerrar Cuadre del Dia
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

// ============== HELPER COMPONENTS ==============

const SummaryCard = ({ icon, label, value, sublabel, color, testId }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`rounded-xl border-2 p-3 ${colors[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1 opacity-70">{icon}<span className="text-xs font-bold uppercase">{label}</span></div>
      <p className="text-xl sm:text-2xl font-black">{value}</p>
      {sublabel && <p className="text-xs opacity-60 font-semibold">{sublabel}</p>}
    </div>
  );
};

const MetodoPagoBadge = ({ metodo }) => {
  const styles = {
    efectivo: 'bg-green-100 text-green-800 border-green-300',
    zelle: 'bg-purple-100 text-purple-800 border-purple-300',
    tarjeta: 'bg-blue-100 text-blue-800 border-blue-300',
    credito: 'bg-amber-100 text-amber-800 border-amber-300',
    pago_rd: 'bg-orange-100 text-orange-800 border-orange-300',
    transferencia_rd: 'bg-teal-100 text-teal-800 border-teal-300',
  };
  const labels = {
    efectivo: 'Efectivo', zelle: 'Zelle', tarjeta: 'Tarjeta',
    credito: 'Credito', pago_rd: 'Pago RD', transferencia_rd: 'Transfer RD',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${styles[metodo] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
      {labels[metodo] || metodo}
    </span>
  );
};

const DesgloseRow = ({ label, value, color, icon }) => {
  const textColors = { green: 'text-green-600', purple: 'text-purple-600', blue: 'text-blue-600', gray: 'text-gray-600' };
  const bgColors = { green: 'bg-green-50', purple: 'bg-purple-50', blue: 'bg-blue-50', gray: 'bg-gray-50' };
  return (
    <div className={`flex justify-between items-center p-2.5 rounded-lg ${bgColors[color]}`}>
      <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">{icon} {label}</span>
      <span className={`text-lg font-bold ${textColors[color]}`}>${(value || 0).toFixed(2)}</span>
    </div>
  );
};

export default CuadreDiario;
